import { useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileSpreadsheet, Copy, RefreshCw, ExternalLink } from "lucide-react";

const ROWS = [
  { key: "daily_sales", title: "Penjualan Harian", desc: "Aggregat per tanggal × outlet: sales, HPP, gross profit, jumlah transaksi (30 hari terakhir)." },
  { key: "transactions", title: "Detail Transaksi", desc: "Setiap transaksi dengan no, waktu, outlet, kasir, total, metode." },
  { key: "inventory", title: "Snapshot Inventory", desc: "Produk & bahan baku beserta stok, HPP, status kritis/aman." },
];

export default function SheetsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/sheets/token");
      setData(data);
    } catch (e) { toast.error("Gagal ambil token"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function copy(text) {
    navigator.clipboard.writeText(text);
    toast.success("Formula disalin. Tempel di Google Sheets.");
  }

  const fullUrl = (path) => `${BACKEND_URL}${path}`;
  const importFormula = (path) => `=IMPORTDATA("${fullUrl(path)}")`;

  return (
    <div className="page-shell">
      <div data-workspace-redundant-header><div>
        <h1 className="text-2xl font-extrabold font-display">Google Sheets Link</h1></div>
        <p className="text-slate-500 text-sm">Auto-export data MAZBOY HUB ke Google Sheets — data akan segar setiap kali sheet dibuka.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="w-4 h-4"/>Cara Pakai</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          <ol className="list-decimal pl-5 space-y-1 text-slate-700">
            <li>Buka Google Sheets baru di <a href="https://sheets.new" target="_blank" rel="noreferrer" className="text-orange-600 underline">sheets.new</a></li>
            <li>Klik salah satu tombol <b>Salin Formula</b> di bawah</li>
            <li>Paste formula di sel <code className="bg-slate-100 px-1 rounded">A1</code> Google Sheet Anda</li>
            <li>Data akan otomatis muncul dan refresh setiap ~1 jam</li>
          </ol>
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            🔑 Token bersifat rahasia — siapa saja yang punya URL ini bisa lihat data Anda. Jangan share ke publik.
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3">
        {ROWS.map(r => {
          const path = data?.endpoints?.[r.key];
          const formula = path ? importFormula(path) : "-";
          return (
            <Card key={r.key} data-testid={`sheets-${r.key}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{r.title}</CardTitle>
                    <p className="text-xs text-slate-500 mt-1">{r.desc}</p>
                  </div>
                  <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700">Live</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="p-3 bg-slate-950 text-emerald-400 rounded-lg font-mono text-xs break-all">{formula}</div>
                <div className="flex gap-2">
                  <Button data-testid={`sheets-copy-${r.key}`} onClick={()=>copy(formula)} className="flex-1 bg-orange-500 hover:bg-orange-600"><Copy className="w-4 h-4 mr-1"/>Salin Formula</Button>
                  {path && <Button variant="outline" onClick={()=>window.open(fullUrl(path), "_blank")}><ExternalLink className="w-4 h-4 mr-1"/>Preview CSV</Button>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex justify-center">
        <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className="w-4 h-4 mr-1"/>Regenerate token</Button>
      </div>
    </div>
  );
}
